import React, { useEffect, useRef, useState } from "react";

// =====================================================================
// FIFA Verified badge — design D-A "FIFA Verified Badge · Variante 1d"
// (handoff board, approved by Marin 2026-08-04). Replaces the old
// self-declared "Licensed" chip EVERYWHERE it rendered.
//
// BADGE RULE (Marin 2026-08-04, supersedes the earlier verified-only
// truth rule): "Ich hatte doch gesagt auch die aktuellen Selbstauskünfte
// erhalten FIFA Badge, man muss manuell eingeben können wie jetzt!" —
// self-declaration carries the badge; only an explicit failed check
// hides it. The badge renders when the agent has a license on file
// (self-declared `license_number` / `is_licensed`, manual entry as
// today) OR AgentLicenseVerification is 'fifa_verified'. SINGLE
// exception (lead default, documented here as the Marin rule +
// exception): status 'failed_qr' — an explicitly failed document check
// whose №66 mail went out — renders NOTHING, otherwise the profile
// would contradict the mail. pending_review / unverified / no
// verification row WITH a number still show the badge. There is still
// NO negative variant and ONE badge look for everyone.
//
// Board recipe (exact values):
//   size "lg" — H 32, profile header:   FIFA + Verified + check 13, pad 14
//   size "md" — H 24, cards / search:   FIFA + check 10, pad 10
//               (solid md carries the "Verified" word, per the dark-card
//               sample on the board)
//   size "sm" — H 20, rows / dropdowns: FIFA + check 9, pad 7 (icon-only)
//   tone: light surfaces = outline (white bg, rgba(33,131,254,.45) border,
//   #1A6FE0 text); dark surfaces = solid (#2183FE bg, white). Pass
//   `solid` where the badge sits on a dark context — the ONE colour is
//   always TP blue #2183FE, no further variants.
//
// Tooltip: "FIFA Verified Agent" — desktop via title (+ aria-label for
// AT), mobile via tap (click toggles the board's dark bubble). The badge
// wordmark is TEXT, never the FIFA logo.
//
// i18n: the badge text and tooltip are FIXED ENGLISH in all languages
// (Marin, D-A §4). Twofold enforcement: `data-no-translate="true"` on
// every text-bearing node (the DOM translator skips them), and the
// static bundle `translations/components/common/fifa-verified-badge.json`
// carries the identical English value for every locale column.
// =====================================================================

export const FIFA_VERIFIED_TOOLTIP = "FIFA Verified Agent";

// The one gate every surface uses. Marin 2026-08-04: self-declaration
// carries the badge; only an explicit failed check hides it.
//   (license on file AND status !== 'failed_qr') OR status === 'fifa_verified'
// Accepts an agent-like object carrying `license_verification_status`
// plus the self-declared signals (`license_number` and/or `is_licensed` —
// some payloads, e.g. the change-agent dropdown, only ship `is_licensed`).
// A raw status string can't prove a license on file, so it stays
// verified-only (no product surface passes bare strings).
export const isFifaVerified = (agentOrStatus) => {
  const status =
    typeof agentOrStatus === "string"
      ? agentOrStatus
      : agentOrStatus?.license_verification_status;
  if (status === "fifa_verified") return true;
  if (typeof agentOrStatus === "string" || !agentOrStatus) return false;
  if (status === "failed_qr") return false;
  return (
    agentOrStatus.is_licensed === true ||
    Boolean(String(agentOrStatus.license_number || "").trim())
  );
};

const SIZES = {
  lg: {
    height: 32,
    padding: "0 14px",
    gap: 7,
    fifa: { fontSize: 13, letterSpacing: "0.05em" },
    verified: { fontSize: 13 },
    check: 13,
    checkStroke: 1.8,
    showVerified: () => true,
  },
  md: {
    height: 24,
    padding: "0 10px",
    gap: 5,
    fifa: { fontSize: 11, letterSpacing: "0.06em" },
    verified: { fontSize: 11 },
    check: 10,
    checkStroke: 2,
    // The dark-card sample on the board carries the word; the light md
    // chip is FIFA + check only.
    showVerified: (solid) => solid,
  },
  sm: {
    height: 20,
    padding: "0 7px",
    gap: 4,
    fifa: { fontSize: 7.5, letterSpacing: "0.1em" },
    verified: { fontSize: 7.5 },
    check: 9,
    checkStroke: 2,
    showVerified: () => false,
  },
};

const FifaVerifiedBadge = ({
  size = "md",
  solid = false,
  className = "",
  style,
}) => {
  const spec = SIZES[size] || SIZES.md;
  const [tipOpen, setTipOpen] = useState(false);
  const hideTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  // Mobile: tap toggles the tooltip bubble; it dismisses itself so a tap
  // never leaves a stuck overlay. Desktop hover keeps the native title.
  const toggleTip = () => {
    clearTimeout(hideTimerRef.current);
    setTipOpen((open) => {
      const next = !open;
      if (next) {
        hideTimerRef.current = setTimeout(() => setTipOpen(false), 2500);
      }
      return next;
    });
  };

  const checkStroke = solid ? "#FFFFFF" : "#2183FE";

  return (
    <span
      data-no-translate="true"
      title={FIFA_VERIFIED_TOOLTIP}
      aria-label={FIFA_VERIFIED_TOOLTIP}
      role="img"
      onClick={toggleTip}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        flex: "none",
        gap: spec.gap,
        height: spec.height,
        padding: spec.padding,
        borderRadius: 999,
        background: solid ? "#2183FE" : "#FFFFFF",
        border: solid ? "none" : "1px solid rgba(33,131,254,0.45)",
        color: solid ? "#FFFFFF" : "#1A6FE0",
        cursor: "default",
        whiteSpace: "nowrap",
        ...style,
      }}
      className={className}
    >
      {tipOpen && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 7px)",
            left: 0,
            zIndex: 40,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            pointerEvents: "none",
          }}
        >
          <span
            data-no-translate="true"
            style={{
              background: "#0B1626",
              color: "#fff",
              font: "500 12px/1 'Helvetica Neue', Helvetica, sans-serif",
              padding: "7px 11px",
              borderRadius: 8,
              whiteSpace: "nowrap",
            }}
          >
            {FIFA_VERIFIED_TOOLTIP}
          </span>
          <span
            aria-hidden="true"
            style={{
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #0B1626",
              marginLeft: 16,
            }}
          />
        </span>
      )}
      <span
        data-no-translate="true"
        style={{
          fontWeight: 800,
          lineHeight: 1,
          fontFamily: "'Helvetica Neue', Helvetica, sans-serif",
          ...spec.fifa,
        }}
      >
        FIFA
      </span>
      {spec.showVerified(solid) && (
        <span
          data-no-translate="true"
          style={{
            fontWeight: 600,
            lineHeight: 1,
            fontFamily: "'Helvetica Neue', Helvetica, sans-serif",
            ...spec.verified,
          }}
        >
          Verified
        </span>
      )}
      <svg
        width={spec.check}
        height={spec.check}
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M2.2 6.6 5 9.2 9.8 3.4"
          stroke={checkStroke}
          strokeWidth={spec.checkStroke}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
};

export default FifaVerifiedBadge;
